import { Component, OnInit, EventEmitter, Output, ViewChild, ElementRef, OnDestroy, Input, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { BaseComponent } from 'src/app/common/base/base.component';
import { PendingResourcesType, Catalog } from 'src/app/common/enumerations/enumerations';
import { DisapprovalReasonComponent } from 'src/app/controls/disapproval-reason/disapproval-reason.component';
import { ResourcesService } from 'src/app/components/catalogs/beamline-catalog/resource/resources.service';
import { PrivilegeEnum } from 'src/app/common/enumerations/privilege-enum';
import { CatalogModel } from '../../catalog-model';
import { CatalogService } from '../../catalog-service';
import { Resource, ResourceSummary, BeamlineShutterRelation, DetailsUsers, ChangeSummary } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { PendingResource } from 'src/app/services/pending-approvals/pending-approval';

@Component({
  selector: 'rss-test-database',
  templateUrl: './rss-test-database.component.html',
  styleUrls: ['./rss-test-database.component.scss']
})
export class RssTestDatabaseComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() beamlineShutter?: BeamlineShutterRelation;
  @Input() showChanges!: boolean;

  @Output() refreshResources = new EventEmitter<any>();
  @Output() pending = new EventEmitter<boolean>();
  @Output() selectionLoadFinished = new EventEmitter<void>();
  @ViewChild('bodyHeader') bodyHeader!: ElementRef;

  valid = false;
  isModifying = false;

  prevResource = false;

  showApproveDisapprove = false;
  approving = false;
  showModifyButton = false;
  showCancelButton = false;

  modifyAvailable = false;

  editPrivilege = false;
  authPrivilege = false;

  shutter: Resource | null = new Resource();
  beamline: Resource | null = new Resource();
  resourceSummary: ResourceSummary = new ResourceSummary();

  currentBeamline!: Resource | null;
  currentShutter!: Resource | null;

  shutterName!: string;
  beamlineName!: string;

  beamlineShutterRelations?: BeamlineShutterRelation[];
  beamlineShutterRelationsFiltered?: BeamlineShutterRelation[];
  beamlineShutterRelations$!: Observable<BeamlineShutterRelation[]>;
  beamlineShutterRelationsSubs!: Subscription;

  pendingResource?: PendingResource;

  public detailResourceUsers: DetailsUsers = { approverUserName: '', updateUserName: '', approvedOn: null, updatedOn: null };
  private selectionLoadVersion = 0;

  constructor(
    protected override injector: Injector,
    public resourceService: ResourcesService,
    public catalogService: CatalogService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const selectionLoadVersion = ++this.selectionLoadVersion;
    this.loadBeamlineShutterRelations(selectionLoadVersion);
  }

  ngOnInit(): void {
    this.checkPrivileges();
    this.clear();
  }

  override ngOnDestroy(): void {
    this.beamlineShutterRelationsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  checkPrivileges() {
    this.editPrivilege = this.hasPrivilege(PrivilegeEnum.RSSTestDBEdit);
    this.authPrivilege = this.hasPrivilege(PrivilegeEnum.RSSTestDBAuth);
  }

  loadBeamlineShutterRelations(selectionLoadVersion = this.selectionLoadVersion) {
    this.beamlineShutterRelationsSubs?.unsubscribe();
    this.beamlineShutterRelations$ = this.store.select(state => state.BeamlineShutter.data);
    this.beamlineShutterRelationsSubs = this.beamlineShutterRelations$.subscribe(data => {
      if (data?.length) {
        this.beamlineShutterRelations = data;
        if (this.beamlineShutter) {
          this.beamlineShutter = this.beamlineShutterRelations.find(r => r.beamlineId == this.beamlineShutter?.beamlineId);
          void this.set(selectionLoadVersion);
        }
      }
    });
  }


  getting = false;

  async set(selectionLoadVersion = this.selectionLoadVersion): Promise<void> {
    const beamlineShutter = this.beamlineShutter;

    if (!beamlineShutter) {
      this.clear();
      this.scrollToTop();
      await this.finishSelectionLoading(selectionLoadVersion);
      return;
    }

    const beamlineId = beamlineShutter.beamlineId;

    if (!beamlineId) {
      this.scrollToTop();
      await this.finishSelectionLoading(selectionLoadVersion);
      return;
    }

    try {
      await this.loadPendingResourceUsers(beamlineShutter);

      if (beamlineId < 0) {
        await this.loadPendingCatalogSummary(beamlineId);
        this.scrollToTop();
        return;
      }

      if (this.getting) {
        this.scrollToTop();
        setTimeout(() => void this.set(selectionLoadVersion), 50);
        return;
      }

      this.getting = true;
      await this.loadResourceSummary(beamlineShutter);
    } catch (error) {
      console.error('Error loading beamline shutter details', error);
    } finally {
      this.getting = false;
      this.scrollToTop();
      await this.finishSelectionLoading(selectionLoadVersion);
    }
  }

  private async finishSelectionLoading(selectionLoadVersion = this.selectionLoadVersion): Promise<void> {
    if (selectionLoadVersion !== this.selectionLoadVersion) return;
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (selectionLoadVersion !== this.selectionLoadVersion) return;
    this.selectionLoadFinished.emit();
  }

  private async loadPendingResourceUsers(beamlineShutter: any): Promise<void> {
    const beamlineId = beamlineShutter?.beamlineId;
    if (!beamlineId || beamlineId < 0) {
      this.resetDetailResourceUsers();
      return;
    }

    const pendingResourceUsers = await firstValueFrom(
      this.catalogService.GetPendingResourcesUsersByResourceId(
        beamlineId,
        PendingResourcesType.Rss_Database
      )
    );

    const approverId = pendingResourceUsers?.approverId;
    const updateById = pendingResourceUsers?.updateById;
    const firstPending = beamlineShutter.pendingResources?.[0];

    if (!updateById && firstPending && this.showChanges) {
      this.detailResourceUsers.approverUserName = '';
      this.detailResourceUsers.approvedOn = null;
      this.detailResourceUsers.updateUserName = this.getUser(firstPending.createdBy)?.name ?? '';
      this.detailResourceUsers.updatedOn = firstPending.createdOn ?? null;
      return;
    }

    if (updateById) {
      this.detailResourceUsers.approverUserName = this.getUser(approverId)?.name ?? '';
      this.detailResourceUsers.updateUserName = this.getUser(updateById)?.name ?? '';
      this.detailResourceUsers.approvedOn = pendingResourceUsers?.approvedOn ?? null;
      this.detailResourceUsers.updatedOn = pendingResourceUsers?.updatedOn ?? null;
      return;
    }

    this.resetDetailResourceUsers();
  }

  private async loadResourceSummary(beamlineShutter: any): Promise<void> {
    const data = await firstValueFrom(
      this.resourceService.getResourceSummaryByBeamline(beamlineShutter.beamlineId)
    );

    this.addResourceSummaryInfo(data);

    this.pendingResource = this.catalogService.getLastPendingResource(
      beamlineShutter,
      PendingResourcesType.Rss_Database
    );

    this.applyPendingResourceState();
    this.onShowPrevResource();
  }

  private async loadPendingCatalogSummary(beamlineId: number): Promise<void> {
    const data = await firstValueFrom(
      this.catalogService.GetPendingCatalogById(beamlineId, Catalog.BeamlineCatalog)
    );

    if (data?.resourceSummary) {
      this.addResourceSummaryInfo(data.resourceSummary);
    }

    this.showApproveDisapprove = this.authPrivilege;
  }

  private applyPendingResourceState(): void {
    this.showModifyButton = true;

    if (
      this.pendingResource &&
      this.authPrivilege &&
      this.currentUser?.id !== this.pendingResource.createdBy
    ) {
      this.prevResource = true;
      this.showApproveDisapprove = true;
      this.showModifyButton = false;
      this.showCancelButton = false;
      return;
    }

    this.showCancelButton = this.currentUser?.id === this.pendingResource?.createdBy;
    this.prevResource = this.currentUser?.id === this.pendingResource?.createdBy;
    this.isModifying = false;
    this.showModifyButton = this.editPrivilege;
    this.modifyAvailable = !this.pendingResource;
    this.showApproveDisapprove = false;
  }

  private resetDetailResourceUsers(): void {
    this.detailResourceUsers.approverUserName = '';
    this.detailResourceUsers.approvedOn = null;
    this.detailResourceUsers.updateUserName = '';
    this.detailResourceUsers.updatedOn = null;
  }

  reset() {
    setTimeout(() => {
      this.clear();
      this.set();
    }, 300);
  }

  clear() {
    this.currentBeamline = null;
    this.currentShutter = null;
    this.resourceSummary = new ResourceSummary();
    this.shutter = new Resource();
    this.beamline = new Resource();
    this.valid = false;
    this.isModifying = false;
    this.showApproveDisapprove = false;
    this.showModifyButton = false;
    this.prevResource = false;
    this.showCancelButton = false;
    this.modifyAvailable = false;

  }

  addResourceSummaryInfo(data: ResourceSummary) {
    this.resourceSummary = data;
    if (data.shutter) {
      this.shutter = data.shutter as Resource;
      this.shutterName = this.shutter.name ?? '';
    }
    this.beamline = data.beamline as Resource;
    this.beamlineName = this.beamline.name ?? '';
  }

  modifyResource() {
    this.showModifyButton = false;
    this.isModifying = true;
  }

  saveResource() {
    if (this.isValid()) {
      const catalogModel = new CatalogModel();
      this.resourceSummary.beamline = this.beamline!;
      this.resourceSummary.shutter = this.shutter!;
      this.resourceSummary.resourceChange = { modified: true } as ChangeSummary;
      catalogModel.resourceSummary = this.resourceSummary;
      this.catalogService.SaveAsDraftType(catalogModel, Catalog.BeamlineCatalog, PendingResourcesType.Rss_Database).subscribe(x => {
        this.alert.warning('The Resource Modification Has Been Sent To Approve Successfully!!');
        this.reset();
      });
    }
  }

  isValid() {
    this.valid = true;

    return this.valid;
  }

  approve() {
    if (this.approving) return;

    if (this.beamlineShutter?.beamlineId && this.pendingResource?.id) {
      this.approving = true;
      this.catalogService.Approve(this.pendingResource.id, Catalog.BeamlineCatalog, PendingResourcesType.Rss_Database).subscribe(
        () => {
          this.alert.success('Resource Approved Successfully!!');
          this.pendingResource = undefined;
          this.showApproveDisapprove = false;
          this.reset();
        },
        error => {
          console.error(error);
          this.approving = false;
        },
        () => this.approving = false
      );
    }
  }

  openAddDisapprovalDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = '';
    dialogConfig.width = '400px';
    const dialogRef = this.dialog.open(DisapprovalReasonComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(data => {
      if (data != undefined) {
        this.disapprove(data);
      }
    });
  }

  disapprove(disapprovalReason: string) {
    if (this.pendingResource?.id)
      this.catalogService.Unapprove(this.pendingResource.id, Catalog.BeamlineCatalog, disapprovalReason, PendingResourcesType.Rss_Database).subscribe(data => {
        this.alert.success('Resource Disapproved Successfully!!');
        this.pendingResource = undefined;
        this.showApproveDisapprove = false;
        this.reset();
      });
  }

  onShowPrevResource() {
    this.pendingResource = this.catalogService.getLastPendingResource(this.beamlineShutter!, PendingResourcesType.Rss_Database);
    
    if (this.showChanges) {
      this.currentBeamline = normalizeKeys(this.beamline, new Resource());
      this.currentShutter = normalizeKeys(this.shutter, new Resource());
      
      const resourceData = this.utils.JSONparse(this.pendingResource?.resourceData);
      if (resourceData) {
        this.beamline = normalizeKeys(resourceData.beamline, new Resource());
        this.shutter = normalizeKeys(resourceData.shutter, new Resource());
      }
    } else {
      this.beamline = normalizeKeys(this.currentBeamline ?? this.beamline, new Resource());
      this.shutter = normalizeKeys(this.currentShutter ?? this.shutter, new Resource());
      this.currentBeamline = null;
      this.currentShutter = null;
    }

    function normalizeKeys(obj: any, model: any) {
      if (!obj) return obj;

      const lowerMap = Object.keys(obj).reduce((acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      }, {} as Record<string, string>);

      const result: any = {};

      for (const modelKey of Object.keys(model)) {
        const match = lowerMap[modelKey.toLowerCase()];
        result[modelKey] = match ? obj[match] : undefined;
      }

      return result;
    }
  }

  isModified(resource: string, prop: string) {
    if (this.currentBeamline && this.currentShutter) {
      switch (resource) {
        case 'shutter':
          const val1 = getPropCaseInsensitive(this.shutter, prop);
          const val2 = getPropCaseInsensitive(this.currentShutter, prop);
          return val1 !== val2;
        case 'beamline':
          if (isScalar((this.beamline as any)[prop])) {
            const val1 = getPropCaseInsensitive(this.beamline, prop);
            const val2 = getPropCaseInsensitive(this.currentBeamline, prop);
            return val1 !== val2;
          } else {
            const obj1 = (this.beamline as any)[prop];
            const obj2 = (this.currentBeamline as any)[prop];
            return !this.utils.isEquivalent(obj1, obj2);
          }
        default:
          return false;
      }
    } else {
      return false;
    }

    function isScalar(value: any): boolean {
      return (
        value === null ||
        value === undefined ||
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
      );
    }

    function getPropCaseInsensitive(obj: any, prop: string) {
      if (!obj) return undefined;

      const key = Object.keys(obj)
        .find(k => k.toLowerCase() === prop.toLowerCase());

      const result = key ? obj[key] : undefined;
      return result ?? undefined;
    }
  }

  cancel() {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to Cancel your changes ?',
        icon: 'warn'
      }
    });
    confirm.afterClosed().subscribe(res => {
      if (res) {
        if (this.beamlineShutter?.beamlineId)
          this.catalogService.Unapprove(this.beamlineShutter.beamlineId, Catalog.BeamlineCatalog, 'User Cancelled Changes', PendingResourcesType.Rss_Database).subscribe(data => {
            this.alert.warning('Changes has been cancelled!');
          });
      }
    });
  }

}
